import { useEffect, useState, useCallback, useRef } from 'react';
import { Socket } from 'socket.io-client';
import socketService, { CallEvent } from '../services/unifiedSocketService';
import socketManager from '../services/socketManager';
import authService from '../services/authService';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { useAuth } from './useAuth';
import { toast } from 'react-hot-toast';
import { logger } from '../utils/logger';
import { useTranslation } from 'react-i18next';

export type CallStatus = 'IDLE' | 'RINGING' | 'IN_CALL' | 'ENDED';

interface Call {
  id: string;
  callerId: string;
  receiverId: string;
  type: 'video' | 'audio';
  status?: string;
}

interface UseSocketReturn {
  callStatus: CallStatus;
  setCallStatus: (status: CallStatus) => void;
  incomingCall: Call | null;
  isConnected: boolean;
  socket: Socket | null;
  startCall: (receiverId: string, type?: 'video' | 'audio') => void;
  acceptCall: (callId: string) => void;
  rejectCall: (callId: string) => void;
  endCall: (callId: string) => void;
}

export const useSocket = (): UseSocketReturn => {
  const { user } = useAuth();
  const { t } = useTranslation('notifications');
  const [callStatus, setCallStatus] = useState<CallStatus>('IDLE');
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);

  // Handler pour les événements d'appels
  const handleCallEvent = useCallback((...args: unknown[]) => {
    const event = args[0] as CallEvent;
    if (!event) return;
    switch (event.status) {
      case 'ringing':
        setIncomingCall({
          id: event.callId,
          callerId: event.callerId || '',
          receiverId: event.receiverId || '',
          type: event.type || 'video',
        });
        setCallStatus('RINGING');
        toast.success(t('call.incoming_generic'));
        break;

      case 'accepted':
        setCallStatus('IN_CALL');
        setIncomingCall(null);
        toast.success(t('call.accepted'));
        break;

      case 'declined':
        setCallStatus('IDLE');
        setIncomingCall(null);
        toast(t('call.declined'));
        break;

      case 'ended':
        setCallStatus('ENDED');
        setIncomingCall(null);
        toast(t('call.ended'));
        setTimeout(() => setCallStatus('IDLE'), 2000);
        break;
    }
  }, [t]);

  // Handler pour la reconnexion automatique - stabilisé avec useRef
  const handleReconnectionRef = useRef<(() => void) | null>(null);

  const handleReconnection = useCallback(() => {
    // La reconnexion est maintenant gérée nativement par Socket.IO
    // et notifiée via unifiedSocketService.
    logger.debug('[useSocket] Reconnection handled by native Socket.IO');
  }, []);

  // Stocker la référence
  handleReconnectionRef.current = handleReconnection;

  // Initialiser la connexion Socket.IO - UNE SEULE FOIS par utilisateur
  useEffect(() => {
    if (!user?.id) return;

    const handleDisconnectRef = { current: null as (() => void) | null };

    const handleDisconnect = () => {
      setIsConnected(false);
      // Notifié de manière centralisée dans unifiedSocketService
    };

    handleDisconnectRef.current = handleDisconnect;

    const initializeSocket = async () => {
      try {
        const token = authService.getToken();
        const userId = user.id;

        if (!token || !userId) {
          logger.warn('Initialisation Socket.IO annulée: token ou userId manquant');
          return;
        }

        // SocketService gère maintenant les connexions multiples
        await socketManager.connect();
        const connected = socketService.isSocketConnected();
        setIsConnected(connected);

        if (connected) {
          socketService.onCallEvent(handleCallEvent);
          reconnectAttempts.current = 0;

          // Sync initial state
          setIsConnected(true);

          // Écouter les déconnexions pour l'UI locale
          socketService.on(SOCKET_EVENTS.DISCONNECT, handleDisconnect);
          socketService.on('connect', () => setIsConnected(true));
          socketService.on('reconnect', () => setIsConnected(true));
        }
      } catch (error) {
        logger.warn("Erreur lors de l'initialisation Socket.IO", error);
        setIsConnected(false);
        if (handleReconnectionRef.current) {
          handleReconnectionRef.current();
        }
      }
    };

    initializeSocket();

    const timeoutRef = reconnectTimeout;
    return () => {
      socketService.off('call:event', handleCallEvent);
      if (handleDisconnectRef.current) {
        socketService.off('disconnect', handleDisconnectRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // Dépendances stabilisées : seulement user.id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Actions d'appels
  const startCall = useCallback(
    (receiverId: string, type: 'video' | 'audio' = 'video') => {
      if (!isConnected) {
        toast.error(t('network.unavailable_retry'));
        return;
      }
      socketService.startCall(receiverId, type);
      setCallStatus('RINGING');
      toast.loading(t('call.calling'));
    },
    [isConnected, t]
  );

  const acceptCall = useCallback(
    (callId: string) => {
      if (!isConnected) {
        toast.error(t('network.unavailable'));
        return;
      }
      socketService.acceptCall(callId);
      setCallStatus('IN_CALL');
      setIncomingCall(null);
    },
    [isConnected, t]
  );

  const rejectCall = useCallback(
    (callId: string) => {
      if (!isConnected) {
        toast.error(t('network.unavailable'));
        return;
      }
      socketService.rejectCall(callId);
      setCallStatus('IDLE');
      setIncomingCall(null);
    },
    [isConnected, t]
  );

  const endCall = useCallback((callId: string) => {
    socketService.endCall(callId);
    setCallStatus('ENDED');
    setTimeout(() => setCallStatus('IDLE'), 2000);
  }, []);

  // Nettoyage lors du démontage
  useEffect(() => {
    const timeoutRef = reconnectTimeout;
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    callStatus,
    setCallStatus,
    incomingCall,
    isConnected,
    socket: socketService.getSocket(),
    startCall,
    acceptCall,
    rejectCall,
    endCall,
  };
};

export default useSocket;
